import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { getModelDisplayName } from '../../constants/models';

// Model colors for consistency
const MODEL_COLORS = ['#00BCD4', '#32CD32', '#F59E0B', '#8B5CF6', '#EC4899', '#10B981'];

/**
 * ComparisonPerformanceChart - Displays model performance comparison
 * Shows average latency and cost per model from comparison data
 */
const ComparisonPerformanceChart = ({ comparisons, loading }) => {
  // Aggregate data by model
  const chartData = useMemo(() => {
    if (!comparisons || comparisons.length === 0) return [];

    const modelStats = {};

    comparisons.forEach(comp => {
      // Process model 1
      if (comp.model_1) {
        if (!modelStats[comp.model_1]) {
          modelStats[comp.model_1] = { latencies: [], costs: [], count: 0 };
        }
        modelStats[comp.model_1].count++;
        if (comp.latency_ms_1) modelStats[comp.model_1].latencies.push(comp.latency_ms_1);
        if (comp.estimated_cost_1) modelStats[comp.model_1].costs.push(comp.estimated_cost_1);
      }

      // Process model 2
      if (comp.model_2) {
        if (!modelStats[comp.model_2]) {
          modelStats[comp.model_2] = { latencies: [], costs: [], count: 0 };
        }
        modelStats[comp.model_2].count++;
        if (comp.latency_ms_2) modelStats[comp.model_2].latencies.push(comp.latency_ms_2);
        if (comp.estimated_cost_2) modelStats[comp.model_2].costs.push(comp.estimated_cost_2);
      }
    });

    // Calculate averages and format for chart
    return Object.entries(modelStats)
      .map(([model, stats]) => {
        const avgLatency = stats.latencies.length > 0
          ? stats.latencies.reduce((a, b) => a + b, 0) / stats.latencies.length
          : 0;
        const avgCost = stats.costs.length > 0
          ? stats.costs.reduce((a, b) => a + b, 0) / stats.costs.length
          : 0;

        // Shorten display name for chart axis
        const displayName = getModelDisplayName(model);
        const shortName = displayName.length > 10 ? displayName.substring(0, 8) + '..' : displayName;

        return {
          model: shortName,
          fullModel: displayName,
          avgLatency: avgLatency / 1000, // Convert to seconds
          avgCost: avgCost,
          count: stats.count
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 6); // Top 6 models
  }, [comparisons]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg p-3 shadow-lg bg-bg-secondary/95 border border-cyan-500/30 backdrop-blur-sm">
          <p className="text-sm font-semibold mb-2 text-text-primary">
            {data.fullModel}
          </p>
          <div className="space-y-1">
            <div className="flex justify-between gap-4">
              <span className="text-xs text-text-tertiary">Avg Latency</span>
              <span className="text-xs font-mono text-cyan-500">
                {data.avgLatency.toFixed(2)}s
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-xs text-text-tertiary">Avg Cost</span>
              <span className="text-xs font-mono text-primary-500">
                ${data.avgCost.toFixed(4)}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-xs text-text-tertiary">Comparisons</span>
              <span className="text-xs font-mono text-text-secondary">
                {data.count}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };

  if (loading) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-40 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] rounded animate-pulse bg-primary-500/5"></div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-cyan-500/25">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Model Performance
        </h3>
        <div className="h-[200px] flex flex-col items-center justify-center">
          <svg className="w-10 h-10 mb-2 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm text-text-tertiary">No comparison data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-cyan-500/25">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Model Performance
          </h3>
          <p className="text-xs mt-0.5 text-text-tertiary">
            Average latency per model
          </p>
        </div>
        <span className="text-xs text-text-tertiary">
          {chartData.length} models
        </span>
      </div>
      
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(50, 205, 50, 0.1)" 
              vertical={false}
            />
            <XAxis 
              dataKey="model"
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#A8D5A2' }}
            />
            <YAxis 
              tickFormatter={(v) => `${v.toFixed(1)}s`}
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 188, 212, 0.08)' }} />
            <Bar 
              dataKey="avgLatency" 
              name="Avg Latency"
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={MODEL_COLORS[index % MODEL_COLORS.length]}
                  style={{ filter: 'drop-shadow(0 0 4px rgba(0, 188, 212, 0.3))' }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

ComparisonPerformanceChart.propTypes = {
  comparisons: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool
};

export default ComparisonPerformanceChart;
